import * as XLSX from 'xlsx';
import DataService from './DataService';

class ExcelQuoteService {
    // En haut de la classe
    static COLUMN_WIDTHS = [
        { wch: 50 }, // Description
        { wch: 15 }, // Prix unitaire
        { wch: 10 }, // Quantité
        { wch: 15 }  // Total HT
    ];

    static EURO_FORMAT = '#,##0.00 €';
    static LIGHT_GREY_FILL = { patternType: "solid", fgColor: { rgb: "DDDDDD" } };
    static BOLD_FONT = { bold: true, sz: 12 };
    static SHEET_NAME = "Lignes du devis";
    static HEADERS = ['Description', 'Prix unitaire', 'Quantité', 'Total HT'];

    /**
     * Génère un fichier Excel à partir des lignes d'un devis
     * @param {string} quoteCustomId - L'ID personnalisé du devis
     */
    async generateQuoteExcel(quoteCustomId) {
        try {
            // Récupérer le devis par son ID personnalisé
            const quote = await DataService.getQuoteByCustomId(quoteCustomId);
            if (!quote) {
                throw new Error('Devis non trouvé');
            }

            // Récupérer les données du client pour le nom du fichier
            const opportunity = await DataService.getById('opportunities', quote.opportunityId);
            const account = opportunity ? await DataService.getById('accounts', opportunity.accountId) : null;
            const clientName = account ? account.name : 'Client';

            // Récupérer les lignes du devis avec leur hiérarchie
            const quoteLines = await DataService.getQuoteLineHierarchy(quote.id);
            
            // Préparer les données Excel
            const excelData = this.prepareQuoteData(quoteLines);
            
            // Générer le nom du fichier au format AAAAMMJJ_clientName_numéro
            // Utilisation du timestamp du devis au lieu de la date actuelle
            const fileName = this.generateFileName(clientName, quoteCustomId, quote.timeStamp);
            
            // Générer et télécharger le fichier
            this.generateExcelFile(excelData, fileName);
            
            return true;
        } catch (error) {
            console.error('Erreur lors de la génération du fichier Excel:', error);
            throw error;
        }
    }
    
    /**
     * Génère le nom de fichier au format AAAAMMJJ_clientName_numéro
     * @param {string} clientName - Nom du client
     * @param {string} documentNumber - Numéro de document
     * @param {Object} timeStamp - Objet TimeStamp du devis
     * @returns {string} Nom du fichier formaté
     */
    generateFileName(clientName, documentNumber, timeStamp) {
        let dateStr = this.formatDateFromTimeStamp(timeStamp);
        
        // Extraire un nom de client propre (sans caractères spéciaux et espaces remplacés par des tirets)
        const cleanClientName = clientName.replace(/[^\w\s]/gi, '').replace(/\s+/g, '-');
        
        return `${dateStr}_${cleanClientName}_${documentNumber}.xlsx`;
    }

    /**
     * Formate une date depuis un objet TimeStamp en chaîne AAAAMMJJ
     * @param {Object} timeStamp - Objet TimeStamp avec year, month, day
     * @returns {string} Date formatée
     */
    formatDateFromTimeStamp(timeStamp) {
        if (timeStamp && timeStamp.year && timeStamp.month && timeStamp.day) {
            // Assurer que month et day sont bien formatés avec des zéros devant si nécessaire
            const month = String(timeStamp.month).padStart(2, '0');
            const day = String(timeStamp.day).padStart(2, '0');
            return `${timeStamp.year}${month}${day}`;
        }
        
        // Fallback sur la date du jour
        const today = new Date();
        return `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
    }

    /**
     * Prépare les données du devis pour le format Excel
     * @param {Array} quoteLines - Les lignes du devis avec leur hiérarchie
     * @returns {Array} Données formatées pour Excel
     */
    prepareQuoteData(quoteLines) {
        // Tableau pour stocker toutes les lignes Excel
        const excelRows = [];
        
        // Traiter toutes les lignes
        this.processLinesRecursively(quoteLines, excelRows);
        
        // Calculer le total général
        const grandTotal = quoteLines.reduce((sum, line) => {
            return sum + this.calculateTotalForLine(line);
        }, 0);
        
        // Ajouter une ligne vide puis la ligne de total
        excelRows.push([null, null, null, null]);
        excelRows.push(['TOTAL', null, null, grandTotal]);
        
        // Ajouter l'en-tête en premier
        excelRows.unshift(ExcelQuoteService.HEADERS);
        
        return excelRows;
    }
    
    /**
     * Calcule récursivement le total d'une ligne et de ses sous-lignes
     * @param {Object} line - La ligne du devis
     * @returns {number} Le total calculé
     */
    calculateTotalForLine(line) {
        if (line.subLines && line.subLines.length > 0) {
            return line.subLines.reduce((total, subLine) => {
                return total + this.calculateTotalForLine(subLine);
            }, 0);
        } else {
            return line.unitPrice * line.quantity;
        }
    }
    
    /**
     * Traite récursivement les lignes de devis pour les convertir en format Excel
     * @param {Array} lines - Les lignes à traiter
     * @param {Array} excelRows - Le tableau de lignes Excel à compléter
     * @param {number} level - Le niveau d'indentation actuel
     */
    processLinesRecursively(lines, excelRows, level = 0) {
        lines.forEach(line => {
            const hasSubLines = line.subLines && line.subLines.length > 0;
            const indent = '    '.repeat(level);
            
            // Calcul du total pour cette ligne
            let totalHT = 0;
            
            if (hasSubLines) {
                // Pour les lignes principales, calculer le total des sous-lignes
                totalHT = this.calculateTotalForLine(line);
                
                // Ajouter la ligne principale
                excelRows.push([
                    indent + line.description,
                    null,
                    null,
                    totalHT === 0 ? null : totalHT
                ]);
                
                // Traiter les sous-lignes récursivement
                this.processLinesRecursively(line.subLines, excelRows, level + 1);
            } else {
                // Ligne simple (sans sous-lignes)
                totalHT = line.unitPrice * line.quantity;
                
                excelRows.push([
                    indent + line.description,
                    line.unitPrice,
                    line.quantity,
                    totalHT === 0 ? null : totalHT
                ]);
            }
        });
    }
    
    /**
     * Génère un fichier Excel à partir des données fournies
     * @param {Array} data - Les données à mettre dans le fichier Excel
     * @param {string} filename - Le nom du fichier à générer
     */
    generateExcelFile(data, filename) {
        // Créer une feuille de calcul à partir du tableau
        const worksheet = XLSX.utils.aoa_to_sheet(data);
        
        // Appliquer les styles de colonne
        this.applyColumnStyles(worksheet);
        
        // Identifier les lignes principales (sans prix unitaire ni quantité)
        const mainRowIndices = this.identifyMainRows(data);
        
        // Appliquer tous les styles
        this.applyStyles(worksheet, mainRowIndices);
        
        // Créer et sauvegarder le classeur
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, ExcelQuoteService.SHEET_NAME);
        
        // Générer et télécharger le fichier
        XLSX.writeFile(workbook, filename);
    }

    /**
     * Identifie les indices des lignes principales
     * @param {Array} data - Les données du tableau
     * @returns {Array} Indices des lignes principales
     */
    identifyMainRows(data) {
        const rowIndices = [];
        for (let i = 1; i < data.length - 1; i++) { // Exclure l'en-tête et la ligne TOTAL
            const row = data[i];
            if (row[0] && row[1] === null && row[2] === null) {
                rowIndices.push(i);
            }
        }
        return rowIndices;
    }

    /**
     * Applique les styles aux cellules du worksheet
     * @param {Object} worksheet - La feuille Excel
     * @param {Array} rowStyles - Les indices des lignes principales
     */
    applyStyles(worksheet, rowStyles) {
        const range = XLSX.utils.decode_range(worksheet['!ref']);
        const headerRowIndex = 0;
        const totalRowIndex = range.e.r;
        
        this.applyColumnStyles(worksheet, range);
        this.applyRowStyles(worksheet, range, headerRowIndex, totalRowIndex, rowStyles);
        this.applyNumberFormats(worksheet, range);
    }

    /**
     * Applique les styles aux colonnes (largeur)
     * @param {Object} worksheet - La feuille Excel
     */
    applyColumnStyles(worksheet) {
        worksheet['!cols'] = ExcelQuoteService.COLUMN_WIDTHS;
    }

    /**
     * Applique les styles aux lignes (en-tête, lignes principales, total)
     * @param {Object} worksheet - La feuille Excel
     * @param {Object} range - La plage de cellules
     * @param {number} headerRowIndex - L'indice de ligne d'en-tête
     * @param {number} totalRowIndex - L'indice de ligne de total
     * @param {Array} mainRowIndices - Les indices des lignes principales
     */
    applyRowStyles(worksheet, range, headerRowIndex, totalRowIndex, mainRowIndices) {
        for (let row = 0; row <= range.e.r; row++) {
            const isHeaderRow = row === headerRowIndex;
            const isTotalRow = row === totalRowIndex;
            const isMainRow = mainRowIndices.includes(row);
            
            for (let col = 0; col <= range.e.c; col++) {
                const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
                
                if (!worksheet[cellRef]) continue;
                if (!worksheet[cellRef].s) worksheet[cellRef].s = {};
                
                if (isHeaderRow || isTotalRow) {
                    worksheet[cellRef].s.font = ExcelQuoteService.BOLD_FONT;
                }
                
                if (isMainRow || isTotalRow) {
                    worksheet[cellRef].s.fill = ExcelQuoteService.LIGHT_GREY_FILL;
                }
            }
        }
    }

    /**
     * Applique les formats numériques (monétaires) aux cellules
     * @param {Object} worksheet - La feuille Excel
     * @param {Object} range - La plage de cellules
     */
    applyNumberFormats(worksheet, range) {
        const priceUnitCol = XLSX.utils.encode_col(1); // Colonne B - Prix unitaire
        const totalCol = XLSX.utils.encode_col(3); // Colonne D - Total HT
        
        for (let row = 1; row <= range.e.r; row++) { // Sauter l'en-tête
            // Format pour la colonne Prix unitaire
            const priceCellRef = priceUnitCol + (row + 1);
            if (worksheet[priceCellRef] && typeof worksheet[priceCellRef].v === 'number') {
                worksheet[priceCellRef].z = ExcelQuoteService.EURO_FORMAT;
            }
            
            // Format pour la colonne Total HT
            const totalCellRef = totalCol + (row + 1);
            if (worksheet[totalCellRef] && typeof worksheet[totalCellRef].v === 'number') {
                worksheet[totalCellRef].z = ExcelQuoteService.EURO_FORMAT;
            }
        }
    }
}

export default ExcelQuoteService;